import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { MST4040_R01Dto } from './Dto_MST4040_R01';
import { useMST4040_R01Store } from './Store_MST4040_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST4040_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST4040_R01Dto> | null>;
	defaultMST4040_R01Row: MST4040_R01Dto;
}

export const Table_MST4040_R01 = (props: Props) => {
	const { gridRef, defaultMST4040_R01Row } = props;

	const {
		MST4040_R01List,
		selectedMST4040_R01,
		setSelectedMST4040_R01,
		initSelectedMST4040_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useMST4040_R01Store();

	const { moldClassList } = useCommonCodeStore();
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'moldCd',
		gridData: MST4040_R01List,
		defaultGridData: defaultMST4040_R01Row,
		selectedRow: selectedMST4040_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		moldCd: '금형코드',
		moldNm: '금형명',
		moldClass: '금형구분',
		currentShot: '현재타수',
		shotLimit: '한계타수',
		itemCd: '생산품목',
		itemNm: '품명',
		cavity: 'CAVITY',
		remark: '비고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'moldCd')(params), []);

	const baseColumnDefs: ColDef<MST4040_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '금형코드', colId: 'moldCd', field: 'moldCd', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'moldCd'),
		},
		{
			headerName: '금형명', colId: 'moldNm', field: 'moldNm', width: 160,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'moldNm'),
		},
		{
			headerName: '금형구분', colId: 'moldClass', field: 'moldClass', width: 110,
			headerClass: aggridHeaderClass,
			valueFormatter: (p) => {
				const code = p.data?.moldClass;
				if (!code) return '';
				return moldClassList.find((value) => value.minorCd === code)?.codeNm || '';
			},
			cellStyle: (p) => getCellStyle(p, 'moldClass'),
		},
		{
			headerName: '현재타수', colId: 'currentShot', field: 'currentShot', width: 100,
			type: 'numericColumn',
			headerClass: aggridHeaderClass,
			valueFormatter: (p) =>
				p.data?.currentShot != null ? Number(p.data.currentShot).toLocaleString() : '',
			cellStyle: (p) => getCellStyle(p, 'currentShot'),
		},
		{
			headerName: '한계타수', colId: 'shotLimit', field: 'shotLimit', width: 110,
			type: 'numericColumn',
			headerClass: aggridHeaderClass,
			valueFormatter: (p) =>
				p.data?.shotLimit != null ? Number(p.data.shotLimit).toLocaleString() : '',
			cellStyle: (p) => getCellStyle(p, 'shotLimit'),
		},
		{
			headerName: '생산품목', colId: 'itemCd', field: 'itemCd', width: 130,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 160,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: 'CAVITY', colId: 'cavity', field: 'cavity', width: 90,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'cavity'),
			cellRenderer: (p: { data?: MST4040_R01Dto }) => p.data?.cavity?.toLocaleString(),
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'remark'),
		},
	], [getCellStyle, moldClassList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST4040_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST4040_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST4040_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedMST4040_R01?.moldCd === rowData.moldCd;
		setSelectedMST4040_R01(isSelected ? undefined : rowData);
	}, [selectedMST4040_R01, setSelectedMST4040_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		moldCd: `${MST4040_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [MST4040_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [MST4040_R01List, selectedMST4040_R01]);

	return (
		<GridContextWrapper<MST4040_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedMST4040_R01}
			defaultRowData={defaultMST4040_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST4040_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST4040_R01Dto>({
					idKey: 'moldCd',
					selectedList: selectedMST4040_R01 ? [selectedMST4040_R01.moldCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
